/* ─────────────────────────────────────────────────────────────────────────────
 * Request Types Registry — the extensible backbone of the Approval Center / Inbox.
 *
 * Every kind of request that can require action (attendance corrections, leave,
 * overtime, …) is described here as a *type adapter*. Each adapter knows:
 *   • how to FETCH its source data (using existing, unchanged APIs)
 *   • how to NORMALISE a raw record into the shared `InboxItem` shape
 *   • which inline ACTIONS it supports (approve / reject / requestInfo), if any
 *   • where to deep-link for the full module experience (openHref)
 *
 * To add a new request source in the future you ONLY add an entry to TYPES below
 * (set `enabled: true` and provide a `fetch`). Nothing in the Inbox UI changes.
 *
 * Nothing here mutates or replaces existing endpoints — adapters are additive
 * read/action callers over the same APIs the rest of the app already uses.
 * ──────────────────────────────────────────────────────────────────────────── */
import EditCalendarRoundedIcon     from '@mui/icons-material/EditCalendarRounded';
import EventAvailableRoundedIcon    from '@mui/icons-material/EventAvailableRounded';
import MoreTimeRoundedIcon          from '@mui/icons-material/MoreTimeRounded';
import EventBusyRoundedIcon         from '@mui/icons-material/EventBusyRounded';
import FlightTakeoffRoundedIcon     from '@mui/icons-material/FlightTakeoffRounded';
import PaidRoundedIcon              from '@mui/icons-material/PaidRounded';
import DescriptionRoundedIcon       from '@mui/icons-material/DescriptionRounded';
import AssignmentRoundedIcon        from '@mui/icons-material/AssignmentRounded';
import BadgeRoundedIcon             from '@mui/icons-material/BadgeRounded';
import PersonAddAlt1RoundedIcon     from '@mui/icons-material/PersonAddAlt1Rounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

import apiService, { authFetch } from '../../services/apiServices';
import {
  LEAVE_TYPES, LEAVE_TYPE_LABEL, leaveTypeOptions, leaveAllowance, MATERNITY_LEAVE_TYPE_ID,
  splitForMaternity,
} from '../../config/leaveTypes';
import globals from '../../services/globals';

/* ── Normalised status buckets (drive the tabs) ──────────────────────────────
 * pending      → submitted / waiting on more info
 * for_approval → actionable: awaiting an approve/reject decision
 * approved / rejected → final
 */
export const NORM = {
  pending:      { key: 'pending',      tone: 'warning' },
  for_approval: { key: 'for_approval', tone: 'info'    },
  approved:     { key: 'approved',     tone: 'success' },
  rejected:     { key: 'rejected',     tone: 'error'   },
};

// A single actionable queue ("Për aprovim") groups both `for_approval` (awaiting
// a decision) and `pending` (submitted / awaiting info) so nothing that still
// needs attention gets split across two look-alike tabs.
export const TABS = ['all', 'for_approval', 'approved', 'rejected'];
export const TAB_STATUSES = {
  all:          null, // every status
  for_approval: ['for_approval', 'pending'],
  approved:     ['approved'],
  rejected:     ['rejected'],
};

/* ── Request categories ──────────────────────────────────────────────────────
 * The Approval Center splits its queue in two, because leave is the decision an
 * approver makes most often and it is judged on different grounds (balance,
 * coverage, dates) than everything else. `other` is the deliberate catch-all —
 * a new type joins it automatically unless it is listed under `leave` here.
 */
export const CATEGORIES = ['all', 'leave', 'other'];
const LEAVE_TYPE_IDS = ['leave_request', 'absence_request'];

/** Category of a request type id. Unknown ids fall into `other`. */
export const categoryOf = (typeId) => (LEAVE_TYPE_IDS.includes(typeId) ? 'leave' : 'other');

/* ── Auth + tiny fetch helper (same contract as the existing corrections page) ── */
const API = process.env.REACT_APP_API_URL || '';

function readStoredUser() {
  try {
    const raw = sessionStorage.getItem('logedInUser') || localStorage.getItem('logedInUser') || '';
    if (raw) return JSON.parse(raw);
    const cookie = document.cookie.split(';').find((c) => c.trim().startsWith('logedInUser='));
    if (cookie) return JSON.parse(decodeURIComponent(cookie.split('=').slice(1).join('=')));
  } catch { /* ignore */ }
  return {};
}
function authHeaders() { const t = readStoredUser()?.token || ''; return t ? { Authorization: `Bearer ${t}` } : {}; }

async function apiJSON(path, opts = {}) {
  try {
    const r = await fetch(`${API}${path}`, {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      ...opts,
      body: opts.body && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
    });
    const json = await r.json().catch(() => ({}));
    if (!r.ok || json.success === false) return { ok: false, error: json.error || r.statusText, data: null };
    return { ok: true, data: json.data ?? json, status: r.status };
  } catch (e) {
    return { ok: false, error: e?.message || 'Network error', data: null };
  }
}

// Multipart upload for a leave's medical certificate. No Content-Type header so
// the browser sets the multipart boundary itself; only the auth token is added.
async function uploadLeaveAttachment(id, file) {
  try {
    const fd = new FormData();
    fd.append('attachment', file);
    const r = await fetch(`${API}/api/holidays/${id}/upload-attachment`, {
      method: 'POST', headers: { ...authHeaders() }, body: fd,
    });
    const json = await r.json().catch(() => ({}));
    if (!r.ok || json.success === false) return { ok: false, error: json.error || r.statusText };
    return { ok: true, data: json.data };
  } catch (e) {
    return { ok: false, error: e?.message || 'Network error' };
  }
}

/* ── Identity / role context shared by all adapters ──────────────────────────── */
export function getInboxContext() {
  const stored = readStoredUser();
  const user = stored?.data || stored || {};
  const workerId = String(stored?.worker_id ?? stored?.WorkerId ?? user?.worker_id ?? user?.WorkerId ?? '');
  const roles = [
    user?.user_role, user?.role, user?.Role,
    ...((user?.roles || []).map((r) => (r?.name || r?.role_name || r || '').toString())),
  ].map((r) => (r || '').toString().toLowerCase().trim()).filter(Boolean);
  // Keyword match (not exact) so variants like "administrator", "HR", "supervisor",
  // "drejtor", "owner" all unlock approve/reject here. The backend still authorises
  // every decision, so this only governs which buttons we show.
  const MANAGER_RE = /admin|manager|menaxher|menaxhe|supervisor|mbikeqyr|mbikëqyr|hr|burime|team.?lead|lead|drejtor|owner|pronar|ceo|director|hr_manager/;
  const isManager = roles.some((r) => MANAGER_RE.test(r));
  return { user, workerId, isManager, roles };
}

/* Combine a date + time into an ISO string for attendance-correction payloads. */
const buildIso = (d, t) => (!d || !t ? null : `${d}T${t}:00`);

/** Half-day and hour-based leave live inside one calendar day. */
const isOvertimeLeave = v => LEAVE_TYPES.some(t => t.id === Number(v?.HolidayType) && t.code === 'overtime_hours');
const isSingleDay = (v) => v?.Duration === 'morning' || v?.Duration === 'afternoon' || v?.Duration === 'hours';

/**
 * Effective days a request costs, by the same rules `submit()` uses.
 * Kept next to them on purpose: the recap chip, the over-balance warning and
 * the payload must never disagree about how long a leave is.
 */
function effectiveLeaveDays(v) {
  if (v?.Duration === 'hours') {
    const h = Number(v.LeaveHours);
    return h > 0 ? +(h / 8).toFixed(3) : 0;
  }
  if (isSingleDay(v)) return 0.5;
  // Lehonia është periudhë kalendarike, jo grumbull ditësh pune: zgjat 12 muaj
  // pa u ndërprerë nga fundjavat, dhe lejimi i saj (365) është po ashtu
  // kalendarik. Ta numëroje me ditë pune do të ruante ~260 kundrejt një lejimi
  // prej 365 — dy njësi të ndryshme në të njëjtën kolonë.
  if (Number(v?.HolidayType) === MATERNITY_LEAVE_TYPE_ID) {
    return calendarDaysBetween(v?.StartDate, v?.EndDate);
  }
  return workingDaysBetween(v?.StartDate, v?.EndDate);
}

/** Ditë kalendarike, të dy skajet përfshirëse. */
function calendarDaysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
}

function workingDaysBetween(start, end) {
  if (!start || !end) return 0;
  const s = new Date(start), e = new Date(end);
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  let count = 0; const d = new Date(s);
  while (d <= e) { const day = d.getDay(); if (day !== 0 && day !== 6) count++; d.setDate(d.getDate() + 1); }
  return count;
}

/* ── Leave balance ──────────────────────────────────────────────────────────── */

/**
 * The REAL remaining days, from the server.
 *
 * Everything below `loadLeaveBalance` is a browser-side estimate: allowance
 * minus what was used this year. It knows nothing about the three components
 * that actually make up an entitlement — days carried over from last year
 * (`leave_balances.carried_over`), the seniority bonus, and manual HR
 * corrections — so it under-reports the balance and warns about an overage
 * that may not exist.
 *
 * `/api/holidays/balance-check` runs `services/leaveSummary.js`, the same source
 * that feeds the "Balanca e pushimeve" report and "Pushimet e mia". It also
 * returns the authoritative verdict (`exceeds` / `blocked`), so the warning here
 * and the server's decision at submit time can no longer disagree.
 *
 * Only annual leave: for the other types `leaveAdjustmentBalance` reads a
 * `leave_balances` row that mostly does not exist yet and answers 0, which would
 * flag every marital or blood-donation request as over balance. Those keep the
 * local estimate until their balances are actually maintained.
 */
async function serverLeaveBalance({ empId, type, startDate, endDate, days }) {
  if (!empId || Number(type) !== 1 || !startDate || !endDate) return null;
  try {
    const qs = new URLSearchParams({
      workerId: String(empId),
      leaveTypeId: String(Number(type) || 1),
      startDate: String(startDate).slice(0, 10),
      endDate: String(endDate).slice(0, 10),
      days: String(days ?? 0),
    });
    const r = await authFetch(`/api/holidays/balance-check?${qs}`);
    if (!r.ok) return null;
    const j = await r.json().catch(() => ({}));
    return j?.success === false ? null : (j?.data || null);
  } catch { return null; }
}

/* ── Lehonia: dy faza, dy dokumente ───────────────────────────────────────────
 * Kërkesa bëhet dy herë: një herë para lindjes, për të nisur pushimin, dhe një
 * herë pas saj, kur lindja vërtetohet. Secila fazë ka dokumentin e vet, dhe
 * dokumenti i takon punonjëses — ndaj ruhet te dosja e saj (`worker_files`),
 * jo vetëm te kërkesa, që të mbetet aty edhe pasi kërkesa të mbyllet. */
export const MATERNITY_PHASES = [
  {
    value: 'pre',
    label: 'Para lindjes — kërkesa fillestare',
    doc: 'Raporti mjekësor i shtatzënisë',
    hint: 'Raporti që vërteton shtatzëninë dhe datën e pritshme të lindjes.',
    fileTitle: 'Lehoni — raport mjekësor i shtatzënisë',
  },
  {
    value: 'post',
    label: 'Pas lindjes — vërtetimi',
    doc: 'Certifikata e lindjes',
    hint: 'Certifikata e lindjes së fëmijës ose fleta e lëshimit nga materniteti.',
    fileTitle: 'Lehoni — certifikata e lindjes',
  },
];
const maternityPhase = (v) =>
  MATERNITY_PHASES.find((p) => p.value === v?.MaternityPhase) || MATERNITY_PHASES[0];

/**
 * Ruaj dokumentin te dosja e punonjëses. Kategoria `maternity` e mban të
 * gjetshëm te "Dosjet"; titulli e thotë fazën, që dy dokumentet të mos ngatërrohen.
 */
async function fileToWorkerDossier(workerId, file, { title, description }) {
  if (!workerId || !file) return { ok: false };
  try {
    const fd = new FormData();
    // Emri i fushës është `document` — kështu e pret multer-i te serveri.
    fd.append('document', file);
    fd.append('title', title);
    fd.append('category', 'maternity');
    if (description) fd.append('description', description);
    const r = await fetch(`${API}/api/workers/${workerId}/files`, {
      method: 'POST', headers: { ...authHeaders() }, body: fd,
    });
    const json = await r.json().catch(() => ({}));
    if (!r.ok || json.success === false) return { ok: false, error: json.error || r.statusText };
    return { ok: true, data: json.data };
  } catch (e) {
    return { ok: false, error: e?.message || 'Network error' };
  }
}

/**
 * Periudhat e lehonisë — 6 muaj të paguar + 3, dhe SIPAS ZGJEDHJES edhe 3 muaj
 * pa pagesë, sipas `services/maternityLeave.js`.
 *
 * Asnjë rregull lehonie nuk jeton në browser: përqindjet, paguesi dhe zinxhiri
 * i datave vijnë të llogaritura nga serveri, me normat e marra nga cilësimet e
 * kompanisë (`settings.maternity_pay_rates` / `_payers`). Këtu vetëm shfaqen.
 *
 * Bishti 3-mujor pa pagesë NUK aplikohet vetvetiu: dërgohet `includeUnpaid`
 * vetëm kur përdoruesi e ka zgjedhur, dhe serveri e ka parazgjedhjen OFF edhe
 * po të mos vinte fare.
 *
 * Paga hiqet me qëllim nga kjo thirrje: formulari i kërkesës nuk është vendi ku
 * lexohet rroga e dikujt, dhe shërbimi kthen `pay: null` kur nuk i jepet.
 */
async function maternityPeriods(startDate, includeUnpaid = false) {
  const d = String(startDate || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  try {
    const r = await authFetch(
      `/api/maternity-leave/calculate?startDate=${encodeURIComponent(d)}`
      + (includeUnpaid ? '&includeUnpaid=1' : ''));
    if (!r.ok) return null;
    const j = await r.json().catch(() => ({}));
    return j?.success === false ? null : (j?.data || null);
  } catch { return null; }
}

// Returns { label, allowance, used, remaining } for a leave type + employee in the
// current year — or null when there isn't enough context to compute it.
async function loadLeaveBalance(empId, type) {
  const t = Number(type) || 1;
  if (!empId) return null;
  try {
    const [settings, hRes] = await Promise.all([
      globals().getSettings?.() ?? Promise.resolve(null),
      apiService().query('getEmployeesHolidays', `id=${empId}`),
    ]);
    const allowance = leaveAllowance(t, settings);
    const year = new Date().getFullYear();
    const rows = (hRes?.data || hRes || []).filter((h) => {
      const ht = Number(h.HolidayType ?? h.holidayType);
      const sd = h.StartDate || h.startDate;
      return ht === t && sd && new Date(sd).getFullYear() === year;
    });
    // 3 dhjetore — mbledhja/zbritja float e gjysmë-ditëve lë artefakte (2.5999…).
    const r3 = (n) => Math.round(n * 1000) / 1000;
    const used = r3(rows.reduce((s, h) => s + (Number(h.HolidayDays ?? h.holidayDays) || 0), 0));
    const remaining = allowance === Infinity ? Infinity : Math.max(0, r3(allowance - used));
    // `count` = how many leave requests of this type already exist this year —
    // used to flag the mandatory medical certificate after 3 sick leaves.
    return { label: LEAVE_TYPE_LABEL[t] || '', allowance, used, remaining, count: rows.length };
  } catch { return null; }
}

/* ── Small formatting + mapping helpers ──────────────────────────────────────── */
const dOnly = (v) => (!v ? '—' : new Date(v).toLocaleDateString('sq-AL', { year: 'numeric', month: '2-digit', day: '2-digit' }));

const CORRECTION_TYPE_LABEL = {
  correction: 'Korrigjim i përgjithshëm', forgot_checkin: 'Harrova check-in',
  forgot_checkout: 'Harrova check-out', wrong_time: 'Orë e gabuar',
  overtime: 'Orë shtesë', schedule_change: 'Ndryshim orari',
  break_correction: 'Korrigjim te pauzes', other: 'Korrigjim prezence',
};

const mapCorrectionStatus = (s) => ({
  open: 'for_approval', in_review: 'for_approval', needs_info: 'pending',
  approved: 'approved', rejected: 'rejected',
}[s] || 'pending');

const mapLeaveStatus = (s) => ({
  pending_supervisor: 'for_approval', pending_hr: 'for_approval', pending: 'for_approval',
  approved: 'approved', supervisor_rejected: 'rejected', hr_rejected: 'rejected', rejected: 'rejected',
}[s] || 'for_approval');

/* Two-stage approval ladder for leave (Team Leader → HR), derived from the raw
 * record so the stepper mirrors real backend state. Stage status is one of:
 * approved | rejected | pending (actionable now) | locked (not reached yet) |
 * blocked (will never run because an earlier stage rejected).               */
function leaveStages(h = {}) {
  const s = String(h.Status || '').toLowerCase();
  const supAt = h.SupervisorDecisionAt || h.SupervisorReviewedAt;
  const hrAt = h.HrDecisionAt || h.ReviewedAt;
  const supNote = h.SupervisorNotes || h.SupervisorReason || '';
  const hrNote = h.HrNotes || h.ReviewNotes || '';

  let sup = 'pending';
  let hr = 'locked';
  if (s === 'supervisor_rejected' || s === 'rejected') { sup = 'rejected'; hr = 'blocked'; }
  else if (s === 'pending_supervisor' || s === 'pending') { sup = 'pending'; hr = 'locked'; }
  else if (s === 'pending_hr') { sup = 'approved'; hr = 'pending'; }
  else if (s === 'approved') { sup = 'approved'; hr = 'approved'; }
  else if (s === 'hr_rejected') { sup = 'approved'; hr = 'rejected'; }

  return [
    { role: 'supervisor', status: sup, at: sup === 'pending' || sup === 'locked' ? null : supAt, note: supNote },
    { role: 'hr', status: hr, at: hr === 'pending' || hr === 'locked' || hr === 'blocked' ? null : hrAt, note: hrNote },
  ];
}

const mapOvertimeStatus = (s) => (String(s).toLowerCase() === 'approved' ? 'approved'
  : String(s).toLowerCase().includes('reject') ? 'rejected' : 'for_approval');

const mapGeneralStatus = (s) => ({ pending: 'for_approval', approved: 'approved', rejected: 'rejected' }[s] || 'for_approval');
const GENERAL_CATEGORY_LABEL = {
  document: 'Dokument / Vërtetim', equipment: 'Pajisje / Mjete', it: 'Mbështetje IT',
  finance: 'Financa', hr: 'Burime njerëzore', access: 'Qasje / Autorizim', other: 'Tjetër',
};
const PRIORITY_LABEL = { low: 'I ulët', normal: 'Normal', high: 'I lartë' };

/* ── Normalisers ─────────────────────────────────────────────────────────────── */
function normalizeCorrection(c) {
  const before = c.original_check_in || c.original_check_out;
  return {
    uid: `attendance_correction:${c.id}`,
    type: 'attendance_correction',
    id: c.id,
    refNumber: c.request_number || '',
    title: CORRECTION_TYPE_LABEL[c.type] || 'Korrigjim prezence',
    workerName: c.worker_name || '—',
    department: c.department || c.department_name || '',
    date: c.request_date,
    createdAt: c.created_at,
    rawStatus: c.status,
    status: mapCorrectionStatus(c.status),
    priority: c.priority || 'normal',
    description: c.description || '',
    meta: [
      c.req_check_in  && { label: 'Check-in i kërkuar',  value: new Date(c.req_check_in).toLocaleString('sq-AL') },
      c.req_check_out && { label: 'Check-out i kërkuar', value: new Date(c.req_check_out).toLocaleString('sq-AL') },
      (c.req_break_min != null) && { label: 'Pauzë (min)', value: c.req_break_min },
      before && { label: 'Origjinali', value: [c.original_check_in, c.original_check_out].filter(Boolean).map((x) => new Date(x).toLocaleString('sq-AL')).join(' → ') },
    ].filter(Boolean),
    history: [
      c.created_at && { label: 'Dorëzuar', at: c.created_at, by: c.worker_name },
      c.assigned_to_name && { label: `Caktuar te ${c.assigned_to_name}`, at: c.updated_at },
      c.escalated_to_hr && { label: 'Eskaluar te HR', at: c.updated_at },
      c.reviewed_by_name && { label: `Shqyrtuar nga ${c.reviewed_by_name}`, at: c.updated_at, note: c.review_notes },
    ].filter(Boolean),
    decidedBy: c.reviewed_by_name || '', decidedAt: c.reviewed_at || c.updated_at || '', decisionNote: c.review_notes || '',
    raw: c,
  };
}

/* ── Kërkesa për personel ─────────────────────────────────────────────────── */
const STAFF_TYPE_LABEL = {
  full_time: 'Me orar të plotë', part_time: 'Me orar të pjesshëm',
  contract: 'Me kontratë', seasonal: 'Sezonal', internship: 'Praktikë',
};
const STAFF_REASON_LABEL = {
  replacement: 'Zëvendësim', growth: 'Rritje', project: 'Projekt',
  seasonal: 'Sezonale', other: 'Tjetër',
};

function normalizeStaffRequest(r) {
  const headline = `${r.Headcount}× ${r.PositionTitle}`;
  return {
    uid: `staff_request:${r.Id}`,
    type: 'staff_request',
    id: r.Id,
    title: headline,
    // Kërkesa nuk i takon një punonjësi, i takon një eprori: te kolona e
    // punëtorit qëndron kush e kërkoi, jo kush do të punësohet.
    workerName: r.RequestedBy || r.SubmittedBy || '—',
    date: r.CreatedAt,
    createdAt: r.CreatedAt,
    rawStatus: r.Status,
    status: mapGeneralStatus(r.Status),
    priority: 'normal',
    description: r.Justification || '',
    meta: [
      r.DepartmentName && { label: 'Departamenti', value: r.DepartmentName },
      { label: 'Numri', value: r.Headcount },
      r.EmploymentType && { label: 'Angazhimi', value: STAFF_TYPE_LABEL[r.EmploymentType] || r.EmploymentType },
      r.Reason && { label: 'Arsyeja', value: STAFF_REASON_LABEL[r.Reason] || r.Reason },
      r.ReplacingWorkerName && { label: 'Zëvendëson', value: r.ReplacingWorkerName },
      r.ProjectName && { label: 'Projekti', value: r.ProjectName },
      r.NeededBy && { label: 'Duhet deri', value: dOnly(r.NeededBy) },
      // Ura drejt rekrutimit: pa të, askush s'e di nëse konkursi u hap.
      r.JobPostingId && { label: 'Konkursi', value: `#${r.JobPostingId} — hapur si draft` },
    ].filter(Boolean),
    history: [
      r.CreatedAt && { label: 'Dorëzuar', at: r.CreatedAt, by: r.SubmittedBy || r.RequestedBy },
      r.DecidedAt && { label: r.Status === 'approved' ? 'Aprovuar' : r.Status === 'rejected' ? 'Refuzuar' : 'Vendim',
                       at: r.DecidedAt, by: r.DecidedBy, note: r.DecisionNote },
    ].filter(Boolean),
    decidedBy: r.DecidedBy || '', decidedAt: r.DecidedAt || '', decisionNote: r.DecisionNote || '',
    raw: r,
  };
}

function normalizeGeneral(g) {
  return {
    uid: `general_request:${g.Id}`,
    type: 'general_request',
    id: g.Id,
    title: g.Title || 'Kërkesë e përgjithshme',
    workerName: g.WorkerName || g.SubmittedBy || '—',
    date: g.CreatedAt,
    createdAt: g.CreatedAt,
    rawStatus: g.Status,
    status: mapGeneralStatus(g.Status),
    priority: g.Priority || 'normal',
    description: g.Description || '',
    meta: [
      g.Category && { label: 'Kategoria', value: GENERAL_CATEGORY_LABEL[g.Category] || g.Category },
      g.Priority && { label: 'Prioriteti', value: PRIORITY_LABEL[g.Priority] || g.Priority },
    ].filter(Boolean),
    history: [
      g.CreatedAt && { label: 'Dorëzuar', at: g.CreatedAt, by: g.SubmittedBy },
      g.DecidedAt && { label: g.Status === 'approved' ? 'Aprovuar' : g.Status === 'rejected' ? 'Refuzuar' : 'Vendim', at: g.DecidedAt, by: g.DecidedBy, note: g.DecisionNote },
    ].filter(Boolean),
    decidedBy: g.DecidedBy || '', decidedAt: g.DecidedAt || '', decisionNote: g.DecisionNote || '',
    raw: g,
  };
}

function normalizeLeave(h, ctx) {
  const workerName = ctx.workersMap?.[h.WorkerId] || h.EmployeeName || (h.Employee ? String(h.Employee) : '—');
  const days = h.holidayDays ?? h.HolidayDays ?? h.DaysCount;
  return {
    uid: `leave_request:${h.Id ?? h.id}`,
    type: 'leave_request',
    id: h.Id ?? h.id,
    title: LEAVE_TYPE_LABEL[Number(h.HolidayType)] || 'Kërkesë për pushim',
    workerName,
    department: h.DepartmentName || '',
    date: h.StartDate,
    createdAt: h.CreatedAt || h.RequestedAt || h.StartDate,
    rawStatus: h.Status,
    status: mapLeaveStatus(h.Status),
    priority: 'normal',
    description: h.Note || h.Reason || '',
    meta: [
      { label: 'Periudha', value: `${dOnly(h.StartDate)} – ${dOnly(h.EndDate)}` },
      h.OvertimeMinutes != null ? { label: 'Orë overtime', value: h.OvertimeMinutes / 60 } : days != null && { label: 'Ditë', value: days },
      h.OvertimeTransactionId && { label: 'Overtime Transaction', value: `#${h.OvertimeTransactionId}` },
    ].filter(Boolean),
    history: [
      (h.CreatedAt || h.RequestedAt) && { label: 'Dorëzuar', at: h.CreatedAt || h.RequestedAt, by: workerName },
      h.SupervisorDecisionAt && { label: 'Vendim supervizori', at: h.SupervisorDecisionAt },
      (h.HrDecisionAt || h.ReviewedAt) && { label: 'Vendim HR', at: h.HrDecisionAt || h.ReviewedAt, note: h.ReviewNotes },
    ].filter(Boolean),
    // Medical certificate for sick leave. Absorbed from the retired
    // "Miratimi i pushimeve" module — an approver has to be able to open the
    // evidence before deciding.
    attachmentUrl: h.AttachmentUrl ? `${API}/api/holidays/${h.Id ?? h.id}/attachment` : null,
    decidedBy: h.HrDecisionBy || h.ReviewedBy || h.SupervisorDecisionBy || '',
    decidedAt: h.HrDecisionAt || h.ReviewedAt || h.SupervisorDecisionAt || '',
    decisionNote: h.ReviewNotes || h.HrNotes || h.SupervisorNotes || h.SupervisorReason || '',
    raw: h,
  };
}

/* Full decision history for one leave request, in ActivityTimeline shape.
 * Also absorbed from the retired approvals module; the list feed carries only
 * the two decision timestamps, this carries every recorded event.
 * Returns [] on any failure (403 for non-participants is expected). */
const LEAVE_AUDIT_LABEL = {
  created: 'Dorëzuar', submitted: 'Dorëzuar',
  supervisor_approved: 'Aprovuar nga mbikëqyrësi', supervisor_rejected: 'Refuzuar nga mbikëqyrësi',
  hr_approved: 'Aprovuar nga HR', hr_rejected: 'Refuzuar nga HR',
  hr_override: 'Mbivendosje nga HR', cancelled: 'Anuluar',
  attachment_uploaded: 'Fleta mjekësore u ngarkua',
};
export async function fetchLeaveAuditLog(holidayId) {
  const r = await apiJSON(`/api/holidays/${holidayId}/audit-log`);
  if (!r.ok) return [];
  const rows = Array.isArray(r.data) ? r.data : (r.data?.log || []);
  return rows.map((e) => ({
    label: LEAVE_AUDIT_LABEL[e.action] || e.action || '—',
    at:    e.created_at,
    by:    e.actor_name || e.actor_email || '',
    note:  e.reason || '',
  }));
}

function normalizeOvertime(o, ctx) {
  const workerName = ctx.workersMap?.[o.WorkerId] || o.EmployeeName || '—';
  return {
    uid: `overtime_request:${o.Id ?? o.id}`,
    type: 'overtime_request',
    id: o.Id ?? o.id,
    title: 'Kërkesë për orë shtesë',
    workerName,
    department: o.DepartmentName || '',
    date: o.Date,
    createdAt: o.CreatedAt || o.Date,
    rawStatus: o.Status,
    status: mapOvertimeStatus(o.Status),
    priority: 'normal',
    description: o.Reason || '',
    meta: [
      o.HoursActual != null && { label: 'Orë', value: o.HoursActual },
      o.YTDHours != null && { label: 'YTD', value: o.YTDHours },
    ].filter(Boolean),
    history: [(o.CreatedAt || o.Date) && { label: 'Dorëzuar', at: o.CreatedAt || o.Date, by: workerName }].filter(Boolean),
    decidedBy: o.ReviewedBy || o.DecidedBy || '', decidedAt: o.ReviewedAt || o.DecidedAt || '', decisionNote: o.ReviewNotes || '',
    raw: o,
  };
}

/* ── Type registry ───────────────────────────────────────────────────────────── */
export const TYPES = [
  {
    id: 'general_request',
    color: '#6366F1',
    Icon: AssignmentRoundedIcon,
    enabled: true,
    async fetch() {
      const r = await apiJSON('/api/general-requests');
      if (!r.ok) return [];
      const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      return rows.map(normalizeGeneral);
    },
    actions: {
      async approve(item, { note } = {}) {
        return apiJSON(`/api/general-requests/${item.id}/approve`, { method: 'POST', body: { note: note || undefined } });
      },
      async reject(item, { note } = {}) {
        return apiJSON(`/api/general-requests/${item.id}/reject`, { method: 'POST', body: { note: note || undefined } });
      },
    },
    // In-place creation (popup) — posts to /api/general-requests.
    create: {
      initial: () => ({ Category: 'other', Title: '', Description: '', Priority: 'normal', WorkerId: '' }),
      fields: [
        /**
         * Për kë është kërkesa.
         *
         * Formulari e hapte gjithmonë për veten, ndaj HR-ja dhe eprori nuk
         * kishin nga ta regjistronin një kërkesë që punëtori e bëri me gojë,
         * me telefon ose në letër — dhe ato kërkesa mbeteshin jashtë sistemit.
         * Fusha shfaqet vetëm për ata që mund ta bëjnë; e lënë bosh do të thotë
         * «për veten», si më parë.
         *
         * Serveri e rikontrollon: `employees.manage`/`edit.company`, ose lidhja
         * e vërtetë e eprorit. Kjo fushë vetëm e kërkon, nuk e lejon.
         */
        { name: 'WorkerId', label: 'Për punëtorin', kind: 'worker',
          when: (ctx) => Boolean(ctx?.isManager),
          help: 'Lëre bosh për ta hapur në emrin tënd.' },
        { name: 'Category', label: 'Kategoria', kind: 'select', half: true, options: [
          { value: 'document',  label: 'Dokument / Vërtetim' },
          { value: 'equipment', label: 'Pajisje / Mjete' },
          { value: 'it',        label: 'Mbështetje IT' },
          { value: 'finance',   label: 'Financa' },
          { value: 'hr',        label: 'Burime njerëzore' },
          { value: 'access',    label: 'Qasje / Autorizim' },
          { value: 'other',     label: 'Tjetër' },
        ] },
        { name: 'Priority', label: 'Prioriteti', kind: 'select', half: true, options: [
          { value: 'low', label: 'I ulët' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'I lartë' },
        ] },
        { name: 'Title', label: 'Titulli', kind: 'text', required: true, placeholder: 'p.sh. Kërkesë për vërtetim page' },
        { name: 'Description', label: 'Përshkrimi', kind: 'textarea', required: true, placeholder: 'Përshkruaj kërkesën tënde…' },
      ],
      async submit(v) {
        if (!String(v.Title || '').trim()) return { ok: false, error: 'Titulli është i detyrueshëm.' };
        if (!String(v.Description || '').trim()) return { ok: false, error: 'Përshkrimi është i detyrueshëm.' };
        return apiJSON('/api/general-requests', { method: 'POST', body: {
          Category: v.Category || 'other',
          Title: String(v.Title).trim(),
          Description: String(v.Description).trim(),
          Priority: v.Priority || 'normal',
          // Bosh → serveri e merr punëtorin e vetë kërkuesit.
          ...(v.WorkerId ? { WorkerId: Number(v.WorkerId) } : {}),
        } });
      },
    },
  },
  {
    /**
     * Kërkesa për personel — eprori kërkon staf të ri, HR vendos.
     *
     * Më parë kjo bëhej me email, me mesazh ose me gojë: nuk kishte gjurmë se
     * kush e kërkoi, kur, pse dhe për sa veta, dhe kur HR-ja hapte një konkurs
     * askush s'e dinte nga erdhi. Këtu rri si çdo kërkesë tjetër — në të njëjtën
     * kokë, me të njëjtin miratim dhe të njëjtën gjurmë.
     */
    id: 'staff_request',
    color: '#0EA5E9',
    Icon: PersonAddAlt1RoundedIcon,
    enabled: true,
    async fetch() {
      const r = await apiJSON('/api/staff-requests');
      if (!r.ok) return [];
      const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      return rows.map(normalizeStaffRequest);
    },
    actions: {
      async approve(item, { note } = {}) {
        return apiJSON(`/api/staff-requests/${item.id}/approve`, { method: 'POST', body: { note: note || undefined } });
      },
      async reject(item, { note } = {}) {
        return apiJSON(`/api/staff-requests/${item.id}/reject`, { method: 'POST', body: { note: note || undefined } });
      },
    },
    create: {
      // Vetëm ata që drejtojnë punë e hapin: serveri kërkon ose të drejtën
      // përkatëse, ose të paktën një vartës të vërtetë.
      when: (ctx) => Boolean(ctx?.isManager),
      initial: () => ({ PositionTitle: '', Headcount: 1, EmploymentType: 'full_time', Reason: 'growth', NeededBy: '', Justification: '' }),
      fields: [
        { name: 'PositionTitle', label: 'Pozita e kërkuar', kind: 'text', required: true,
          placeholder: 'p.sh. Agjent i Call Center-it',
          help: 'Shkruaje lirisht — pozita mund të mos ekzistojë ende te lista.' },
        { name: 'Headcount', label: 'Sa persona', kind: 'number', half: true,
          bounds: () => ({ min: 1, max: 50 }) },
        { name: 'EmploymentType', label: 'Angazhimi', kind: 'select', half: true, options: [
          { value: 'full_time',  label: 'Me orar të plotë' },
          { value: 'part_time',  label: 'Me orar të pjesshëm' },
          { value: 'contract',   label: 'Me kontratë' },
          { value: 'seasonal',   label: 'Sezonal' },
          { value: 'internship', label: 'Praktikë' },
        ] },
        { name: 'Reason', label: 'Arsyeja', kind: 'select', half: true, options: [
          { value: 'growth',      label: 'Rritje e volumit' },
          { value: 'replacement', label: 'Zëvendësim' },
          { value: 'project',     label: 'Projekt' },
          { value: 'seasonal',    label: 'Nevojë sezonale' },
          { value: 'other',       label: 'Tjetër' },
        ] },
        { name: 'NeededBy', label: 'Duhet deri më', kind: 'date', half: true },
        /**
         * Për cilin projekt.
         *
         * Shumica e kërkesave vijnë për një projekt — «na duhen tre agjentë për
         * klientin X». Pa këtë fushë, arsyetimi e thoshte me fjalë dhe asnjë
         * raport s'mund ta gruponte: sa veta kërkoi ky projekt, sa u miratuan.
         * Lista lexohet nga serveri; e shkruar në kod do të dilte jashtë
         * sinkronit që te projekti i dytë.
         */
        { name: 'ProjectId', label: 'Projekti', kind: 'combo',
          placeholder: 'Shkruaj emrin e projektit…',
          help: 'Opsionale. Projektet e tua dalin të parat; me një të vetëm, propozohet gati.',
          loadOptions: async () => {
            // Dy lista, jo një: e vetja vendos renditjen dhe propozimin, e plota
            // siguron që asgjë të mos jetë e paarritshme. Lista e vetja mund të
            // dështojë pa e prishur fushën — atëherë mbetet vetëm e plota.
            const [all, mine] = await Promise.all([
              apiJSON('/api/projects'),
              apiJSON('/api/projects/mine').catch(() => ({ data: [] })),
            ]);
            const rows = Array.isArray(all.data) ? all.data : (all.data?.rows || []);
            const mineIds = new Set((Array.isArray(mine.data) ? mine.data : []).map((p) => String(p.Id ?? p.id)));
            return rows
              .map((p) => ({ value: p.Id ?? p.id, label: p.Name ?? p.name, mine: mineIds.has(String(p.Id ?? p.id)) }))
              .filter((o) => o.value && o.label)
              .sort((a, b) => (b.mine - a.mine) || a.label.localeCompare(b.label));
          } },
        // Shfaqet vetëm te zëvendësimi: te rritja do të ishte pyetje pa kuptim.
        { name: 'ReplacingWorkerId', label: 'Zëvendëson punëtorin', kind: 'worker',
          // `showIf` lexon vlerat e formularit; `when` lexon vetëm kontekstin.
          showIf: (values) => values?.Reason === 'replacement',
          help: 'Buxheti i një zëvendësimi lexohet ndryshe nga ai i një vendi të ri.' },
        { name: 'Justification', label: 'Arsyetimi', kind: 'textarea',
          placeholder: 'Pse nevojitet, çfarë nuk mbulohet me stafin aktual, çfarë ndodh nëse nuk plotësohet…' },
      ],
      async submit(v) {
        if (!String(v.PositionTitle || '').trim()) return { ok: false, error: 'Pozita e kërkuar është e detyrueshme.' };
        const n = Number(v.Headcount || 1);
        if (!Number.isFinite(n) || n < 1 || n > 50) return { ok: false, error: 'Numri i personave duhet të jetë 1–50.' };
        if (v.Reason === 'replacement' && !v.ReplacingWorkerId) {
          return { ok: false, error: 'Kur arsyeja është zëvendësim, zgjidh punëtorin që zëvendësohet.' };
        }
        return apiJSON('/api/staff-requests', { method: 'POST', body: {
          PositionTitle: String(v.PositionTitle).trim(),
          Headcount: n,
          EmploymentType: v.EmploymentType || 'full_time',
          Reason: v.Reason || 'growth',
          NeededBy: v.NeededBy || undefined,
          ...(v.ProjectId ? { ProjectId: Number(v.ProjectId) } : {}),
          Justification: String(v.Justification || '').trim() || undefined,
          ...(v.ReplacingWorkerId ? { ReplacingWorkerId: Number(v.ReplacingWorkerId) } : {}),
        } });
      },
    },
  },
  {
    id: 'attendance_correction',
    color: '#3D8FA3',
    Icon: EditCalendarRoundedIcon,
    enabled: true,
    // Not a top-level "Kërkesë e re" entry — it's reached via a toggle inside the
    // general-request dialog. Its `create` config below powers that toggle.
    hideFromCreateMenu: true,
    async fetch() {
      const r = await apiJSON(`/api/attendance-requests?${new URLSearchParams({ sort: 'newest' })}`);
      if (!r.ok) return [];
      const rows = Array.isArray(r.data) ? r.data : (r.data?.rows || r.data?.requests || []);
      return rows.map(normalizeCorrection);
    },
    actions: {
      async approve(item, { note } = {}) {
        const r = await apiJSON(`/api/attendance-requests/${item.id}`, { method: 'PUT', body: { status: 'approved', review_notes: note || undefined } });
        if (r.ok && note) await apiJSON(`/api/attendance-requests/${item.id}/comment`, { method: 'POST', body: { body: note } });
        return r;
      },
      async reject(item, { note } = {}) {
        const r = await apiJSON(`/api/attendance-requests/${item.id}`, { method: 'PUT', body: { status: 'rejected', review_notes: note || undefined } });
        if (r.ok && note) await apiJSON(`/api/attendance-requests/${item.id}/comment`, { method: 'POST', body: { body: note } });
        return r;
      },
      async requestInfo(item, { note } = {}) {
        return apiJSON(`/api/attendance-requests/${item.id}/comment`, { method: 'POST', body: { body: note || 'Ju lutem jepni më shumë detaje.', request_info: true } });
      },
    },
    openHref: () => '/attendance-corrections',
    createHref: () => '/attendance-corrections?create=1',
    // In-place creation (popup) — same payload contract as /attendance-requests.
    create: {
      initial: () => ({ type: 'correction', request_date: '', req_check_in_time: '', req_check_out_time: '', req_break_min: '', description: '', priority: 'normal' }),
      fields: [
        { name: 'type', label: 'Lloji i korrigjimit', kind: 'select', half: true, options: [
          { value: 'correction', label: 'Korrigjim i përgjithshëm' },
          { value: 'forgot_checkin', label: 'Harrova check-in' },
          { value: 'forgot_checkout', label: 'Harrova check-out' },
          { value: 'wrong_time', label: 'Orë e gabuar' },
          { value: 'overtime', label: 'Orë shtesë' },
          { value: 'schedule_change', label: 'Ndryshim orari' },
          { value: 'break_correction', label: 'Korrigjim te pauzes' },
          { value: 'other', label: 'Tjetër' },
        ] },
        { name: 'priority', label: 'Prioriteti', kind: 'select', half: true, options: [
          { value: 'low', label: 'I ulët' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'I lartë' },
        ] },
        { name: 'request_date', label: 'Data', kind: 'date', required: true, half: true },
        { name: 'req_break_min', label: 'Pauza (min)', kind: 'number', half: true },
        { name: 'req_check_in_time', label: 'Hyrja (check-in)', kind: 'time', half: true },
        { name: 'req_check_out_time', label: 'Dalja (check-out)', kind: 'time', half: true },
        { name: 'description', label: 'Përshkrimi', kind: 'textarea', required: true, placeholder: 'Përshkruaj korrigjimin që kërkon…' },
      ],
      async submit(v) {
        if (!v.request_date || !String(v.description || '').trim()) return { ok: false, error: 'Data dhe përshkrimi janë të detyrueshme.' };
        return apiJSON('/api/attendance-requests', { method: 'POST', body: {
          type: v.type || 'correction',
          request_date: v.request_date,
          req_check_in: buildIso(v.request_date, v.req_check_in_time),
          req_check_out: buildIso(v.request_date, v.req_check_out_time),
          req_break_min: v.req_break_min !== '' && v.req_break_min != null ? Number(v.req_break_min) : null,
          description: String(v.description).trim(),
          priority: v.priority || 'normal',
        } });
      },
    },
  },
  {
    id: 'leave_request',
    color: '#3D8FA3', // brand teal — replaces the previous purple to match the app
    Icon: EventAvailableRoundedIcon,
    enabled: true,
    // Two-stage approval ladder surfaced in the detail panel.
    stages: (item) => leaveStages(item.raw),
    async fetch(ctx) {
      try {
        const res = await apiService().get('holidays');
        if (!res?.success) return [];
        let rows = res.data || [];
        // Non-managers only ever see their OWN requests in the inbox.
        if (!ctx.isManager && ctx.workerId) rows = rows.filter((h) => String(h.WorkerId) === String(ctx.workerId));
        return rows.map((h) => normalizeLeave(h, ctx));
      } catch { return []; }
    },
    // Inline approve/reject — routes to the correct stage based on the request's
    // current status (supervisor first, then HR), matching the existing 2-stage
    // /api/holidays decision API (no API change).
    actions: {
      async approve(item, { note } = {}) {
        const stage = item.rawStatus === 'pending_hr' ? 'hr' : 'supervisor';
        return apiJSON(`/api/holidays/${item.id}/${stage}-decision`, { method: 'POST', body: { action: 'approve', reason: (note || '').trim() || undefined } });
      },
      async reject(item, { note } = {}) {
        const reason = (note || '').trim();
        if (!reason) return { ok: false, error: 'Arsyeja është e detyrueshme për refuzim.' };
        const stage = item.rawStatus === 'pending_hr' ? 'hr' : 'supervisor';
        return apiJSON(`/api/holidays/${item.id}/${stage}-decision`, { method: 'POST', body: { action: 'reject', reason } });
      },
    },
    // No openHref: the "Miratimi i pushimeve" module this used to deep-link to
    // was retired, and this Inbox is now the full leave-approval workspace —
    // the two-stage ladder above and the inline actions cover it.
    // New leave request → open the full, professional leave form (balance sidebar,
    // half-day/hours, overlap checks, sick-leave attachments) instead of the
    // minimal in-place dialog. `preferHref` tells the Inbox to navigate there.
    createHref: () => '/holidays/addholiday',
    preferHref: true,
    // In-place creation (popup) — same payload contract as POST /api/holidays.
    create: {
      initial: (ctx) => ({ HolidayType: 1, Employee: ctx?.workerId ? Number(ctx.workerId) : '', StartDate: '', EndDate: '', Duration: 'full', LeaveHours: '', Note: '', Attachment: null, MaternityPhase: 'pre' }),
      fields: [
        // Nga `config/leaveTypes.js` — i njëjti katalog si tabela `leave_types`.
        { name: 'HolidayType', label: 'Lloji i pushimit', kind: 'select', half: true, options: leaveTypeOptions() },
        // Lehonia është e punonjëses, ndaj lista ngushtohet për të. Serveri e
        // refuzon gjithsesi një kërkesë lehonie për një burrë
        // (`services/maternityEligibility.js`) — kjo është mirësjellje, jo gardh.
        //
        // Kush ka gjininë e paplotësuar te profili NUK shfaqet, sepse kërkesa
        // ishte "vetëm gjinia femërore"; por numri i tyre thuhet me zë, që
        // mungesa e dikujt të lexohet si fushë e pambushur dhe jo si person që
        // nuk ekziston.
        { name: 'Employee', label: 'Punonjësi', kind: 'worker', half: true, required: true,
          when: (ctx) => ctx.isManager,
          filterWorkers: (workers, v) => {
            if (Number(v.HolidayType) !== MATERNITY_LEAVE_TYPE_ID) return null;
            const { eligible, hiddenUnknown } = splitForMaternity(workers);
            return {
              list: eligible,
              note: hiddenUnknown > 0
                ? `Vetëm punonjëset. ${hiddenUnknown} punonjës nuk shfaqen sepse gjinia s’është plotësuar te profili.`
                : 'Vetëm punonjëset.',
            };
          } },
        // The picker itself refuses the impossible days: an end before the start,
        // or a start after the end. Half-day and hour leave is one day by
        // definition, so both ends are pinned to the same date.
        { name: 'StartDate', label: 'Data e fillimit', kind: 'date', required: true, half: true,
          bounds: (v) => (isSingleDay(v) ? { max: v.EndDate || undefined } : { max: v.EndDate || undefined }) },
        { name: 'EndDate', label: 'Data e mbarimit', kind: 'date', required: true, half: true,
          bounds: (v) => (isSingleDay(v)
            ? { min: v.StartDate || undefined, max: v.StartDate || undefined }
            : { min: v.StartDate || undefined }) },
        { name: 'Duration', label: 'Kohëzgjatja', kind: 'select', half: true, options: [
          { value: 'full', label: 'Ditë e plotë' },
          { value: 'morning', label: 'Gjysmë dite — paradite' },
          { value: 'afternoon', label: 'Gjysmë dite — pasdite' },
          { value: 'hours', label: 'Me orë (pjesë dite)' },
        ] },
        // Hours deducted from the leave balance — only when "Me orë" is chosen.
        { name: 'LeaveHours', label: 'Orë nga pushimi', kind: 'number', half: true, required: true,
          showIf: (v) => v.Duration === 'hours', placeholder: 'p.sh. 3',
          help: 'Orë që hiqen nga pushimi (1–8). 8 orë = 1 ditë e plotë.' },
        // ── Lehonia: faza dhe dokumenti i saj ─────────────────────────────
        { name: 'MaternityPhase', label: 'Faza', kind: 'select', half: true,
          showIf: (v) => Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID,
          options: MATERNITY_PHASES.map((p) => ({ value: p.value, label: p.label })),
          help: (v) => maternityPhase(v).hint },
        // Bishti 3-mujor pa pagesë — zgjedhje e shprehur, e fikur si parazgjedhje.
        // Ligji e njeh, por nuk e detyron; derisa askush s'e zgjedh, pushimi
        // mbaron pas fazave të paguara dhe data e mbarimit e ndjek atë.
        { name: 'MaternityIncludeUnpaid', label: '3 muaj pa pagesë', kind: 'select', half: true,
          showIf: (v) => Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID,
          options: [
            { value: '',  label: 'Jo — pa fazën pa pagesë' },
            { value: '1', label: 'Po — përfshi edhe 3 muaj pa pagesë' },
          ],
          help: 'Kur zgjidhet "Po", faza pa pagesë hyn në periudhën totale dhe data e mbarimit zgjatet.' },
        { name: 'MaternityDoc', kind: 'file', accept: '.pdf,.jpg,.jpeg,.png',
          showIf: (v) => Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID,
          label: (v) => maternityPhase(v).doc,
          placeholder: (v) => `Ngarko ${maternityPhase(v).doc.toLowerCase()} (PDF, JPG, PNG)`,
          help: 'Ruhet te dosja e punonjëses dhe i bashkëngjitet kërkesës.' },
        { name: 'Note', label: 'Shënim / Arsye', kind: 'textarea', placeholder: 'Arsyeja e pushimit (opsionale)…' },
        // Medical certificate — only for sick leave. Optional at submit time:
        // it may also be uploaded later, after the employee returns to work.
        { name: 'Attachment', label: 'Fleta mjekësore', kind: 'file', accept: '.pdf,.jpg,.jpeg,.png',
          showIf: (v) => Number(v.HolidayType) === 2,
          placeholder: 'Ngarko fletën mjekësore (PDF, JPG, PNG)',
          help: 'Mund ta ngarkoni tani ose pas kthimit në punë.' },
      ],
      /**
       * Balance panel at the top of the dialog, and the figures the over-balance
       * warning judges against.
       *
       * Two sources on purpose: the local estimate supplies `allowance`, `used`
       * and `count` (the progress bar and the sick-note rule), while the server
       * supplies the only number that must be right — `remaining`, which includes
       * days carried over from last year, the seniority bonus and HR corrections.
       * When the server call cannot run (no dates yet, another leave type, a
       * failed request) the local estimate stands and is marked as such.
       */
      loadInfo: async (v, ctx) => {
        const empId = v.Employee || ctx?.workerId;
        if (isOvertimeLeave(v)) {
          const balance = await apiJSON(`/api/overtime/balance?worker_id=${empId}`);
          if (!balance.ok) return { blocked: true, message: balance.error };
          return { unit: 'hours', label: 'Overtime (orë)', allowance: balance.data.BalanceMinutes / 60, used: balance.data.ReservedMinutes / 60, remaining: balance.data.AvailableMinutes / 60, exact: true };
        }

        // Lehonia nuk matet me balancë vjetore — ka periudhat e veta.
        if (Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID) {
          const maternity = await maternityPeriods(v.StartDate, v.MaternityIncludeUnpaid === '1');
          const local = await loadLeaveBalance(empId, v.HolidayType);
          if (!maternity && !local) return null;
          return { ...(local || {}), exact: false, maternity };
        }

        const local = await loadLeaveBalance(empId, v.HolidayType);
        const verdict = await serverLeaveBalance({
          empId, type: v.HolidayType,
          startDate: v.StartDate, endDate: v.EndDate,
          days: effectiveLeaveDays(v),
        });
        if (!local && !verdict) return null;
        if (!verdict) return local ? { ...local, exact: false } : null;
        return {
          ...(local || { label: LEAVE_TYPE_LABEL[1] || '', allowance: null, used: null, count: 0 }),
          remaining: verdict.availableDays,
          exact: true,
          exceeds: !!verdict.exceeds,
          blocked: !!verdict.blocked,
          excessDays: verdict.excessDays,
        };
      },
      infoKeys: ['HolidayType', 'Employee', 'StartDate', 'EndDate', 'Duration', 'LeaveHours',
                 'MaternityIncludeUnpaid'],
      /**
       * Fusha që plotësohen vetë kur janë bosh. Fundin e lehonisë e llogarit
       * serveri — të shkruhet me dorë do të thoshte ta rillogarisësh atë zinxhir
       * në kokë. Nuk mbishkruhet asgjë e shkruar; por kur zgjedhja për fazën pa
       * pagesë ndryshon, ndryshon edhe fundi, dhe një datë që e ka vendosur vetë
       * dialogu duhet ta ndjekë (shih `autofilled` te CreateRequestDialog).
       */
      autofill: (v, info) => (
        Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID && info?.maternity?.overallEnd
          ? { EndDate: info.maternity.overallEnd }
          : null
      ),
      /**
       * Rules that make a request impossible, checked while typing. Each stays
       * quiet until the fields it judges are filled — a form that turns red the
       * moment it opens teaches people to ignore red.
       *
       * These are the client's copy of what POST /api/holidays enforces; the
       * server remains the authority (a request can arrive from anywhere).
       */
      validate: (v) => {
        const { StartDate: s, EndDate: e, Duration: d } = v;
        if (s && e && new Date(e) < new Date(s)) {
          return { text: 'Data e mbarimit është para datës së fillimit.' };
        }
        if (isSingleDay(v) && s && e && s !== e) {
          return { text: d === 'hours'
            ? 'Pushimi me orë zë një ditë të vetme — vendos të njëjtën datë në të dyja fushat.'
            : 'Gjysmë dite zë një ditë të vetme — vendos të njëjtën datë në të dyja fushat.' };
        }
        if (isOvertimeLeave(v) && d !== 'hours') return { text: 'Zgjidh «Me orë» dhe shkruaj orët e sakta. Formula orë → ditë pret vendimin biznesor.' };
        if (d === 'hours' && v.LeaveHours !== '' && v.LeaveHours != null) {
          const h = Number(v.LeaveHours);
          if (!Number.isFinite(h) || h <= 0 || h > (isOvertimeLeave(v) ? 24 : 8)) {
            return { text: 'Orët duhet të jenë mes 1 dhe 8 (8 orë = një ditë e plotë).' };
          }
        }
        // A range that lands entirely on a weekend costs zero days — the request
        // would be stored as 0 and mean nothing.
        if (s && e && !isSingleDay(v)
            && Number(v.HolidayType) !== MATERNITY_LEAVE_TYPE_ID
            && effectiveLeaveDays(v) <= 0) {
          return { text: 'Intervali i zgjedhur nuk përmban asnjë ditë pune.' };
        }
        return null;
      },
      // Contextual notice: after 3 sick leaves in a year, the medical certificate
      // becomes mandatory (still allowed to be uploaded later, not blocking).
      // Advisory only — these do not block sending, they inform the decision.
      alert: (v, info) => {
        // Periudhë e bllokuar (politika e pushimit sipas projektit/departamentit):
        // verdikti i serverit nga balance-check. Strikt → serveri e refuzon te
        // POST, ndaj thuhet si gabim; jo strikt → paralajmërim.
        if (info?.blackouts?.length) {
          const b = info.blackouts.find((x) => x.strict) || info.blackouts[0];
          const span = `${String(b.from).slice(8, 10)}.${String(b.from).slice(5, 7)} – ${String(b.to).slice(8, 10)}.${String(b.to).slice(5, 7)}`;
          return b.strict
            ? { tone: 'error', text: `Periudhë e bllokuar «${b.name}» (${span}): pushimi nuk lejohet në këto data.` }
            : { tone: 'warning', text: `Periudhë e bllokuar «${b.name}» (${span}): kërkesa lejohet, por do të shqyrtohet me kujdes.` };
        }
        // Lehonia: dokumenti i fazës mungon. Paralajmërim, jo bllokim — një
        // certifikatë lindjeje merret pas lindjes, jo para dërgimit të kërkesës.
        if (Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID && !v.MaternityDoc) {
          const p = maternityPhase(v);
          return { tone: 'warning', text: `${p.doc} nuk është ngarkuar. Mund ta ngarkoni tani ose më vonë te "Dosjet" e punonjëses.` };
        }
        if (Number(v.HolidayType) === 2 && info && Number(info.count) >= 3 && !v.Attachment) {
          return { tone: 'warning', text: `Pushimi mjekësor nr. ${Number(info.count) + 1} këtë vit — fleta mjekësore është e detyrueshme. Mund ta ngarkoni tani ose pas kthimit në punë.` };
        }
        // Over the remaining balance — the SERVER's verdict, not a local guess.
        // It counts the days carried over from last year, the seniority bonus and
        // HR corrections, so a request covered by carry-over no longer trips it.
        // Still a warning and not a block: settings.leave_overbalance_mode decides
        // whether the submission is refused, and duplicating that choice here
        // would let the two drift apart.
        const days = effectiveLeaveDays(v);
        if (days > 0 && info?.exact && info.exceeds) {
          const over = info.excessDays ?? Math.round((days - info.remaining) * 100) / 100;
          return {
            tone: info.blocked ? 'error' : 'warning',
            text: info.blocked
              ? `Kërkesa është ${days} ditë, por kanë mbetur ${info.remaining}. Tejkalimi me ${over} ditë nuk lejohet nga cilësimet.`
              : `Kërkesa është ${days} ditë, por kanë mbetur ${info.remaining}. Tejkalim me ${over} ditë — eprori dhe HR-ja njoftohen.`,
          };
        }
        // Backdated leave is normal for sickness, unusual for everything else.
        if (v.StartDate && Number(v.HolidayType) !== 2) {
          const t = new Date(); t.setHours(0, 0, 0, 0);
          if (new Date(v.StartDate) < t) {
            return { tone: 'warning', text: 'Data e fillimit është në të kaluarën — kërkesa do të regjistrohet me atë datë.' };
          }
        }
        return null;
      },
      // Live recap chip shown in the dialog footer as the user fills the form.
      // Same arithmetic as `submit()` and the balance warning — one helper.
      summary: (v) => {
        if (v.Duration === 'hours') { const h = Number(v.LeaveHours); return h > 0 ? (isOvertimeLeave(v) ? `${h} orë` : `${h} orë · ${(h / 8).toFixed(2)} ditë`) : ''; }
        if (isSingleDay(v)) return 'Gjysmë dite · 0.5 ditë';
        const d = effectiveLeaveDays(v);
        if (!d) return '';
        return Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID
          ? `${d} ditë kalendarike`
          : `${d} ditë pune`;
      },
      async submit(v, ctx) {
        const employee = v.Employee || ctx?.workerId;
        if (!employee) return { ok: false, error: 'Punonjësi mungon.' };
        if (!v.StartDate || !v.EndDate) return { ok: false, error: 'Datat e fillimit dhe mbarimit janë të detyrueshme.' };
        if (new Date(v.EndDate) < new Date(v.StartDate)) return { ok: false, error: 'Data e mbarimit nuk mund të jetë para fillimit.' };
        const isHalf = v.Duration === 'morning' || v.Duration === 'afternoon';
        const isHours = v.Duration === 'hours';
        if ((isHalf || isHours) && v.StartDate !== v.EndDate) return { ok: false, error: 'Për gjysmë dite ose orë, data e fillimit dhe e mbarimit duhet të jenë të njëjta.' };
        let leaveHours = null;
        let holidayDays;
        if (isHours) {
          leaveHours = Number(v.LeaveHours);
          if (!leaveHours || leaveHours <= 0 || leaveHours > 8) return { ok: false, error: 'Orët duhet të jenë mes 1 dhe 8.' };
          holidayDays = +(leaveHours / 8).toFixed(3);
        } else if (isHalf) {
          holidayDays = 0.5;
        } else if (Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID) {
          holidayDays = calendarDaysBetween(v.StartDate, v.EndDate);
        } else {
          holidayDays = workingDaysBetween(v.StartDate, v.EndDate);
          // A range wholly on a weekend costs nothing — storing it as 0 days
          // would leave a request that means nothing and blocks nothing.
          if (holidayDays <= 0) return { ok: false, error: 'Intervali i zgjedhur nuk përmban asnjë ditë pune.' };
        }
        const isMaternity = Number(v.HolidayType) === MATERNITY_LEAVE_TYPE_ID;
        const phase = maternityPhase(v);
        // Faza shkruhet te shënimi: `holidays` s'ka kolonë për të, dhe pa këtë
        // dy kërkesat e së njëjtës lehoni duken njësoj te lista e aprovimeve.
        const note = [isMaternity ? `Lehoni — ${phase.label}` : '', (v.Note || '').trim()]
          .filter(Boolean).join(' · ');

        const res = await apiService().post('holidays', {
          Employee: Number(employee),
          HolidayType: Number(v.HolidayType || 1),
          StartDate: v.StartDate,
          EndDate: v.EndDate,
          Note: note || undefined,
          HalfDay: isHalf ? (v.Duration === 'morning' ? 'paradite' : 'pasdite') : null,
          LeaveHours: leaveHours,
          HolidayDays: holidayDays,
          Approve: false,
          CreatedBy: '',
        });
        if (!res?.success) return { ok: false, error: res?.error || res?.message || 'Gabim gjatë ruajtjes.' };
        // Sick-leave certificate (optional): upload after the leave is created.
        // Non-fatal — the request stands even if the file upload fails; it can
        // be attached later from the leave module.
        const newId = res.data?.Id || res.data?.id || res.data?.ID;

        // Fleta mjekësore e pushimit mjekësor — sjellja e mëparshme, e paprekur.
        if (v.Attachment && newId) {
          const up = await uploadLeaveAttachment(newId, v.Attachment);
          if (!up.ok) return { ok: true, data: res.data, warning: 'Kërkesa u dërgua, por ngarkimi i fletës mjekësore dështoi — provoni ta ngarkoni më vonë.' };
        }

        // Dokumenti i lehonisë shkon në DY vende, sepse u shërben dy nevojave:
        // te dosja e punonjëses mbetet përgjithmonë, edhe pasi kërkesa mbyllet;
        // te kërkesa e sheh aprovuesi pa u futur në dosje. Asnjëra s'e rrëzon
        // kërkesën e dërguar — dokumenti ringarkohet më vonë nëse dështon.
        if (isMaternity && v.MaternityDoc) {
          const saved = await fileToWorkerDossier(Number(employee), v.MaternityDoc, {
            title: phase.fileTitle,
            description: `Kërkesë pushimi #${newId || '—'} · ${v.StartDate} – ${v.EndDate}`,
          });
          if (newId) await uploadLeaveAttachment(newId, v.MaternityDoc);
          if (!saved.ok) {
            return { ok: true, data: res.data,
              warning: 'Kërkesa u dërgua, por dokumenti nuk u ruajt te dosja e punonjëses — provoni ta ngarkoni nga "Dosjet".' };
          }
        }
        return { ok: true, data: res.data };
      },
    },
  },
  {
    id: 'overtime_request',
    color: '#F59E0B',
    Icon: MoreTimeRoundedIcon,
    enabled: true,
    async fetch(ctx) {
      try {
        // `getOvertimeRequests` nuk ekziston te serveri: gabimi kapej më poshtë
        // dhe orët shtesë nuk dilnin kurrë te radha, pa e vënë re askush.
        // Dyqani gjenerik `overtimeRequests` është burimi që punon vërtet.
        const res = await apiJSON('/api/overtimeRequests');
        if (!res?.ok) return [];
        let rows = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
        if (!ctx.isManager && ctx.workerId) rows = rows.filter((o) => String(o.WorkerId) === String(ctx.workerId));
        return rows.map((o) => normalizeOvertime(o, ctx));
      } catch { return []; }
    },
    openHref: () => '/home/reports/overtime',
  },
  // ── Registered for future wiring — add `enabled: true` + a `fetch` to activate ──
  { id: 'absence_request',     color: '#EF4444', Icon: EventBusyRoundedIcon,           enabled: false },
  { id: 'business_trip',       color: '#0EA5E9', Icon: FlightTakeoffRoundedIcon,       enabled: false },
  { id: 'payroll_alert',       color: '#10B981', Icon: PaidRoundedIcon,                enabled: false },
  { id: 'document_approval',   color: '#6366F1', Icon: DescriptionRoundedIcon,         enabled: false },
  { id: 'hr_request',          color: '#EC4899', Icon: BadgeRoundedIcon,               enabled: false },
  { id: 'system_notification', color: '#64748B', Icon: NotificationsActiveRoundedIcon, enabled: false },
];

export const TYPE_BY_ID = Object.fromEntries(TYPES.map((t) => [t.id, t]));

/* ── Aggregate fetch across every enabled adapter ────────────────────────────── */
export async function fetchAllRequests(ctx) {
  const enabled = TYPES.filter((t) => t.enabled && typeof t.fetch === 'function');
  const results = await Promise.all(enabled.map(async (t) => {
    try { return await t.fetch(ctx); } catch { return []; }
  }));
  return results.flat().filter(Boolean)
    .sort((a, b) => new Date(b.createdAt || b.date || 0) - new Date(a.createdAt || a.date || 0));
}

/* Load a worker-id → full-name map once, shared by adapters that store worker ids. */
/**
 * Punëtorët, të lexuar NJË herë për të dyja përdorimet.
 *
 * `map` (id → emër) etiketon kërkesat e ardhura; `list` ushqen zgjedhësit e
 * formularit. Deri tani lista nxirrej nga harta, ndaj mbante vetëm emrin — dhe
 * zgjedhësi i lehonisë s'kishte si ta dinte gjininë. Dy thirrje do të thoshin
 * dy përgjigje që mund të mos pajtoheshin; kjo është një.
 */
export async function loadWorkerDirectory() {
  try {
    const res = await apiService().get('workers');
    if (!res?.success) return { map: {}, list: [] };
    const map = {};
    const list = [];
    (res.data || []).forEach((w) => {
      const id = w.Id ?? w.id;
      if (!id) return;
      const name = [w.FirstName ?? w.first_name, w.LastName ?? w.last_name].filter(Boolean).join(' ') || `#${id}`;
      map[id] = name;
      list.push({ id: Number(id), name, gender: w.Gender ?? w.gender ?? null });
    });
    list.sort((a, b) => a.name.localeCompare(b.name));
    return { map, list };
  } catch { return { map: {}, list: [] }; }
}

